import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import { Col, Row, Table, Alert } from "reactstrap";
import { Breadcrumbs, H5 } from "../../AbstractElements";
import { useLoader } from "../../_helper/LoaderContext";
import PrivilegeContext from "../../_helper/PrivilegeContext";
import { useCurrency } from "../../_helper/CurrencyContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { canShowPatientAccounts } from "../../utils/pharmacyConnection";
import CollectPaymentModal from "./components/CollectPaymentModal";
import InvoicePreviewModal from "./components/InvoicePreviewModal";
import InvoiceStatusBadge from "./components/InvoiceStatusBadge";
import PatientAccountPaidAmount from "./components/PatientAccountPaidAmount";
import { getPatientInvoiceDetailsAPI, payRemainingSalesAPI } from "./PatientAccountsService";
import { getGeneralServiceMaster, getLabTestMaster } from "../PharmacySales/SalesService";
import {
	buildPharmacyItemsFromBilling,
	enrichInvestigationsWithMaster,
	enrichServiceItemsWithMaster,
	hasPatientAccountBilling,
	parseGeneralServiceMasterResponse,
	parseLabMasterResponse,
} from "../PharmacySales/NewSales/utils/billingUtils";
import {
	applyPayRemainingSalesResponse,
	buildPatientAccountDetailLines,
	buildPatientAccountFinancialSummary,
	buildPatientAccountPaymentHistory,
	buildPayRemainingSalesPayload,
	buildTreatmentAdditionalServices,
	buildTreatmentInvestigationItems,
	buildTreatmentPackageDetails,
	buildTreatmentPackagePaymentHistory,
	formatAmount,
	formatDateList,
	formatDateTime,
	formatGender,
	formatInvoiceType,
	getCollectPaymentAppointmentId,
	getPatientAccountPatientId,
	getPatientDisplayName,
	isTreatmentPackageInvoice,
	loadPatientAccountRecord,
	normalizePatientAccountItem,
	normalizePaymentStatus,
	savePatientAccountRecord,
} from "./utils/patientAccountUtils";
import { resolveInvoicePresignedUrl } from "../PharmacySales/NewSales/utils/invoiceUrlUtils";
import "./patient-accounts-style.scss";

const MENU_KEY = "patient_accounts";
const SUB_PERMISSION_KEY = "patient_accounts";

const isValidInvoiceRecord = (details) => {
	if (!details || typeof details !== "object") return false;
	if (details.status && details.message && !details._id && !details.appointment_invoice) {
		return false;
	}
	return Boolean(
		details._id ||
			details.appointment_invoice ||
			details.patient ||
			details.package_payment_details
	);
};

const SessionStatusBadge = ({ status }) => {
	const { t } = useTranslation();
	const normalized = String(status || "pending").toLowerCase();
	const isCompleted = normalized === "completed";

	return (
		<span
			className={`badge ${
				isCompleted ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"
			}`}
		>
			{isCompleted ? t("Completed") : t("Pending")}
		</span>
	);
};

const PatientAccountDetails = () => {
	const { invoiceId } = useParams();
	const location = useLocation();
	const { t } = useTranslation();
	const { setLoading } = useLoader();
	const { privileges } = useContext(PrivilegeContext);
	const { getCurrencySymbol, allowedCurrencies } = useCurrency();
	const { pharmacyPOS } = usePharmacy();
	const history = useNavigate();

	const [skeletonLoading, setSkeletonLoading] = useState(true);
	const [record, setRecord] = useState(null);
	const [viewView, setViewView] = useState(false);
	const [collectView, setCollectView] = useState(false);
	const [previewModal, setPreviewModal] = useState(false);
	const [previewUrl, setPreviewUrl] = useState("");
	const [previewTitle, setPreviewTitle] = useState("");
	const [previewLoading, setPreviewLoading] = useState(false);
	const [collectModal, setCollectModal] = useState(false);
	const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
	const [labMasterList, setLabMasterList] = useState([]);
	const [generalMasterList, setGeneralMasterList] = useState([]);

	const getPermission = useCallback(() => {
		if (privileges?.permissions) {
			privileges.permissions.forEach((element) => {
				if (element.menu_key === MENU_KEY && element.sub_permissions) {
					element.sub_permissions.forEach((child) => {
						if (child.key === SUB_PERMISSION_KEY && child.actions) {
							child.actions.forEach((action) => {
								if (action.key === "view" && action.allowed) setViewView(true);
								if ((action.key === "collect_payment" || action.key === "update") && action.allowed) {
									setCollectView(true);
								}
							});
						}
					});
				}
				if (element.menu_key === MENU_KEY && element.actions) {
					element.actions.forEach((action) => {
						if (action.key === "view" && action.allowed) setViewView(true);
						if ((action.key === "collect_payment" || action.key === "update") && action.allowed) {
							setCollectView(true);
						}
					});
				}
			});
		} else {
			setViewView(true);
			setCollectView(true);
		}
	}, [privileges]);

	useEffect(() => {
		getPermission();
	}, [getPermission]);

	useEffect(() => {
		if (!invoiceId) {
			history(`${process.env.PUBLIC_URL}/patient-accounts`);
			return;
		}

		const loaded = loadPatientAccountRecord(invoiceId, location.state);
		if (!loaded) {
			toast.error(t("Invoice details not found. Please open from the list."));
			history(`${process.env.PUBLIC_URL}/patient-accounts`);
			return;
		}

		setRecord(loaded);
		savePatientAccountRecord(invoiceId, loaded);
		setSkeletonLoading(false);
	}, [history, invoiceId, location.state, t]);

	const fetchInvoiceDetails = useCallback(async () => {
		if (!invoiceId) return;

		const clinicId = pharmacyPOS?.hospital_id || "";
		if (!clinicId) return;

		try {
			const response = await getPatientInvoiceDetailsAPI({
				invoice_id: invoiceId,
				invoiceId,
				clinic_id: clinicId,
			});

			if (response?.data?.status === "error") return;

			const details =
				response?.data?.data?.invoice_details ||
				response?.data?.data ||
				response?.data;

			if (isValidInvoiceRecord(details)) {
				setRecord(details);
				savePatientAccountRecord(invoiceId, details);
			}
		} catch (error) {
			console.error("Error fetching patient invoice details:", error);
		}
	}, [invoiceId, pharmacyPOS?.hospital_id]);

	useEffect(() => {
		fetchInvoiceDetails();
	}, [fetchInvoiceDetails]);

	const fetchMasterLists = useCallback(async () => {
		const clinicId = pharmacyPOS?.hospital_id;
		if (!clinicId) return;

		try {
			const [labResponse, generalResponse] = await Promise.all([
				getLabTestMaster({ clinic_id: clinicId, search: "" }),
				getGeneralServiceMaster({ clinic_id: clinicId, search: "" }),
			]);
			setLabMasterList(parseLabMasterResponse(labResponse));
			setGeneralMasterList(parseGeneralServiceMasterResponse(generalResponse));
		} catch (error) {
			console.error("Error loading master lists:", error);
		}
	}, [pharmacyPOS?.hospital_id]);

	useEffect(() => {
		if (isTreatmentPackageInvoice(record)) {
			fetchMasterLists();
		}
	}, [fetchMasterLists, record]);

	const handleBack = () => {
		history(`${process.env.PUBLIC_URL}/patient-accounts`);
	};

	const handleCollectSubmit = async (payload) => {
		const appointmentId =
			payload.appointmentId || getCollectPaymentAppointmentId(record);

		if (!appointmentId) {
			toast.error(t("Appointment not found for this invoice."));
			return;
		}

		try {
			setIsSubmittingPayment(true);
			setLoading(true);
			const invoiceRecord = payload.invoiceRecord || record;
			const response = await payRemainingSalesAPI(
				buildPayRemainingSalesPayload({
					appointmentId,
					paidAmount: payload.paidAmount,
					paymentType: payload.paymentMode,
					currencyType: payload.currency,
					isSettled: payload.markAsPaid,
					isConversion: payload.isConversion ?? payload.currency !== payload.baseCurrency,
					invoiceRecord,
					billing: payload.billing,
					patientId: payload.patientId || getPatientAccountPatientId(invoiceRecord),
					patientName:
						payload.patientName || getPatientDisplayName(invoiceRecord?.patient),
				})
			);
			if (response?.data?.status === "success") {
				toast.success(response.data.message || t("Payment collected successfully."));
				setCollectModal(false);
				const apiData = response.data.data;
				const merged = applyPayRemainingSalesResponse(invoiceRecord, apiData);
				setRecord(merged);
				savePatientAccountRecord(invoiceId, merged);
				if (!apiData?.sales) {
					await fetchInvoiceDetails();
				}
			} else {
				console.log(response?.data?.message);
				// toast.error(response?.data?.message || t("Unable to collect payment."));
			}
		} catch (error) {
			console.error("Error collecting payment:", error);
			// toast.error(
			// 	error?.response?.data?.message || t("Unable to collect payment.")
			// );
		} finally {
			setIsSubmittingPayment(false);
			setLoading(false);
		}
	};

	const normalized = useMemo(
		() => (record ? normalizePatientAccountItem(record) : null),
		[record]
	);

	const financialSummary = useMemo(
		() => buildPatientAccountFinancialSummary(record),
		[record]
	);
	console.log(record)
	const serviceLines = useMemo(() => buildPatientAccountDetailLines(record), [record]);
	const paymentHistory = useMemo(() => buildPatientAccountPaymentHistory(record), [record]);
	const isTreatmentPackage = useMemo(
		() => isTreatmentPackageInvoice(record),
		[record]
	);
	const treatmentPackageDetails = useMemo(
		() => (isTreatmentPackage ? buildTreatmentPackageDetails(record) : null),
		[isTreatmentPackage, record]
	);
	const treatmentPaymentHistory = useMemo(
		() => (isTreatmentPackage ? buildTreatmentPackagePaymentHistory(record) : []),
		[isTreatmentPackage, record]
	);

	const paymentStatus =
		record?.appointment_invoice?.status || record?.billing?.payment?.status || record?.status;
	const isSettled = record?.appointment_invoice?.is_settled === true;
	const normalizedStatus = normalizePaymentStatus(paymentStatus);
	const isPaid = normalizedStatus === "paid";
	const isPartialOrUnpaid = !isPaid && !isSettled;
	const showDueRow = !isSettled && Number(financialSummary.due) > 0;

	const currency = financialSummary.currency || normalized?.currency || "INR";
	const patientName = getPatientDisplayName(record?.patient);
	const fileNumber = record?.patient?.health_id || record?.patient?.file_no || "-";
	const patientAddress = record?.patient?.address || "-";
	const patientGender = formatGender(record?.patient?.gender);
	const contactNumber = record?.patient?.mobile
		? `${record?.patient?.phone_code || ""} ${record.patient.mobile}`.trim()
		: "—";
	const email = record?.patient?.email || "—";
	const doctorName = record?.doctor?.name || "-";
	const doctorContact = record?.doctor?.mobile
		? `${record?.doctor?.phone_code || ""} ${record.doctor.mobile}`.trim()
		: "-";
	const doctorEmail = record?.doctor?.email || "-";
	const doctorGender = formatGender(
		record?.doctor?.gender ||
			record?.package_payment_details?.session_details?.[0]?.session?.appointment
				?.doctor_gender
	);

	const additionalServices = useMemo(() => {
		if (!isTreatmentPackage) return [];
		// Keep pharmacy / lab out of Additional Services — they have their own sections
		const services = buildTreatmentAdditionalServices(record, {
			excludePharmacyAndLab: true,
		});
		if (generalMasterList.length === 0) return services;
		return enrichServiceItemsWithMaster(
			services,
			generalMasterList,
			currency,
			allowedCurrencies
		);
	}, [allowedCurrencies, currency, generalMasterList, isTreatmentPackage, record]);

	const labInvestigations = useMemo(() => {
		if (!isTreatmentPackage) return [];
		const items = buildTreatmentInvestigationItems(record);
		if (items.length === 0) return [];
		return enrichInvestigationsWithMaster(
			items,
			labMasterList,
			currency,
			allowedCurrencies
		);
	}, [allowedCurrencies, currency, isTreatmentPackage, labMasterList, record]);

	const prescriptionBillingItems = useMemo(() => {
		if (!isTreatmentPackage) return [];

		if (hasPatientAccountBilling(record)) {
			const billingPharmacy = buildPharmacyItemsFromBilling(
				record?.billing?.pharmacy_items
			);
			if (billingPharmacy.length > 0) return billingPharmacy;
		}

		const invoicePharmacy = (record?.appointment_invoice?.services || [])
			.filter((item) => String(item.service_type || "").toLowerCase() === "pharmacy")
			.map((item, index) => {
				const qty = Math.max(1, Number(item.quantity) || 1);
				const unitPrice = Number(item.price) || 0;
				const amount = Number(item.amount ?? unitPrice * qty) || 0;
				return {
					item_id: item.service_id || item._id || `pharmacy-${index}`,
					item: item.service_name || "-",
					qty,
					unit_price: unitPrice,
					sub_total: amount,
					tax: 0,
					tax_percentage: 0,
					net_total: amount,
					date: item.date || item.sale_date || "",
				};
			});

		if (invoicePharmacy.length > 0) return invoicePharmacy;

		return (record?.prescriptions || []).map((item, index) => {
			const qty = Math.max(1, Number(item.qty) || 1);
			const unitPrice = Number(item.price) || 0;
			const amount = unitPrice * qty;
			return {
				item_id: item._id || item.medication_id || `rx-${index}`,
				item: item.medication_name || "-",
				medicine_name: item.salt_composition || "",
				product_form: item.product_form || "",
				qty,
				unit_price: unitPrice,
				sub_total: amount,
				tax: 0,
				tax_percentage: 0,
				net_total: amount,
				date: item.date || item.sale_date || "",
			};
		});
	}, [isTreatmentPackage, record]);

	const showPrescriptionSection = prescriptionBillingItems.length > 0;

	const docPaymentHistory =
		record?.package_payment_details?.payment_history ||
		record?.appointment_invoice?.payment_history ||
		[];
	const latestPayment = docPaymentHistory[0] || {};

	const invoiceUrl =
		record?.appointment_invoice?.invoice ||
		record?.appointment_invoice?.package_invoice ||
		record?.package_payment_details?.payment?.invoice ||
		record?.invoiceUrl ||
		record?.invoice_url ||
		record?.invoice ||
		latestPayment.invoice ||
		"";

	const receiptUrl =
		record?.appointment_invoice?.receipt ||
		record?.receiptUrl ||
		record?.receipt_url ||
		record?.receipt ||
		latestPayment.receipt ||
		latestPayment.invoice ||
		"";

	const canPreviewInvoice =
		Boolean(invoiceUrl) && (isPaid || normalizedStatus === "partial_paid");
	const canPreviewReceipt =
		Boolean(receiptUrl) && (isPaid || normalizedStatus === "partial_paid");

	const openDocumentPreview = async (documentKey, title) => {
		const key = String(documentKey || "").trim();
		if (!key) return;

		try {
			setPreviewLoading(true);
			const presignedUrl = await resolveInvoicePresignedUrl(key);
			if (!presignedUrl) {
				toast.warning(t("Preview is not available."));
				return;
			}

			setPreviewUrl(presignedUrl);
			setPreviewTitle(title);
			setPreviewModal(true);
		} catch (error) {
			console.error("Error opening document preview:", error);
			toast.warning(
				error?.response?.data?.message ||
					t("Failed to load preview. Please try again.")
			);
		} finally {
			setPreviewLoading(false);
		}
	};

	const closeDocumentPreview = () => {
		setPreviewModal(false);
		setPreviewUrl("");
		setPreviewTitle("");
	};

	const pageTitle =
		normalizedStatus === "paid"
			? t("View Paid Invoice Details")
			: normalizedStatus === "partial_paid"
				? t("View Partial Invoice Details")
				: t("View Unpaid Invoice Details");

	if (!canShowPatientAccounts(pharmacyPOS)) {
		return (
			<>
				<Breadcrumbs mainTitle={pageTitle} parent={t("Patient Accounts")} title={pageTitle} />
				<Alert color="warning" className="mx-3">
					{t(
						"Patient Accounts is available only when your pharmacy is connected to a hospital and is enabled to collect payments."
					)}
				</Alert>
			</>
		);
	}

	if (!viewView && privileges?.permissions && !skeletonLoading) {
		return (
			<>
				<Breadcrumbs mainTitle={pageTitle} parent={t("Patient Accounts")} title={pageTitle} />
				<div className="card" style={{ borderRadius: "15px" }}>
					<div className="card-body text-center py-5">
						{t("You do not have permission to view this invoice.")}
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<Breadcrumbs mainTitle={pageTitle} parent={t("Patient Accounts")} title={pageTitle} />
			<div className="row patient-accounts-details">
				<div className="col-sm-12">
					<div className="card" style={{ borderRadius: "15px" }}>
						<div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
							<div className="d-flex align-items-center flex-wrap gap-2">
								<H5 className="mb-0">{pageTitle}</H5>
								{isTreatmentPackage && (
									<span className="badge tp-type-badge">{t("Treatment Package")}</span>
								)}
							</div>
							<div className="d-flex flex-wrap gap-2">
								{isTreatmentPackage ? (
									<>
										<button
											type="button"
											className="btn btn-primary"
											disabled={!canPreviewInvoice || !viewView || previewLoading}
											onClick={() =>
												openDocumentPreview(invoiceUrl, t("Invoice Preview"))
											}
										>
											{previewLoading ? (
												<span className="spinner-border spinner-border-sm me-2" role="status" />
											) : (
												<i className="fa fa-file-text-o me-2" />
											)}
											{t("Invoice Preview")}
										</button>
										<button
											type="button"
											className="btn btn-outline-primary"
											disabled={!canPreviewReceipt || !viewView || previewLoading}
											onClick={() =>
												openDocumentPreview(receiptUrl, t("Receipt Preview"))
											}
										>
											{previewLoading ? (
												<span className="spinner-border spinner-border-sm me-2" role="status" />
											) : (
												<i className="fa fa-file-o me-2" />
											)}
											{t("Receipt Preview")}
										</button>
									</>
								) : (
									canPreviewInvoice &&
									viewView && (
										<button
											type="button"
											className="btn btn-primary"
											disabled={previewLoading}
											onClick={() =>
												openDocumentPreview(invoiceUrl, t("Invoice Preview"))
											}
										>
											{previewLoading ? (
												<span className="spinner-border spinner-border-sm me-2" role="status" />
											) : (
												<i className="fa fa-eye me-2" />
											)}
											{t("Invoice Preview")}
										</button>
									)
								)}
								{isPartialOrUnpaid && collectView && (
									<button
										type="button"
										className="btn btn-primary"
										onClick={() => setCollectModal(true)}
									>
										<i className="fa fa-money me-2" />
										{t("Collect Payment")}
									</button>
								)}
								<button
									type="button"
									className="btn btn-outline-secondary"
									onClick={handleBack}
								>
									<i className="fa fa-arrow-circle-o-left me-2 fs-6" />
									{t("Back")}
								</button>
							</div>
						</div>

						<div className="card-body">
							{isTreatmentPackage ? (
								<div className="tp-view">
									<div className="tp-meta-strip mb-3">
										<div className="tp-meta-item">
											<span className="tp-meta-label">{t("Invoice Number")}</span>
											<span className="tp-meta-value">
												{skeletonLoading ? (
													<Skeleton width={130} />
												) : (
													normalized?.invoice_no || "-"
												)}
											</span>
										</div>
										<div className="tp-meta-item">
											<span className="tp-meta-label">{t("Invoice Date")}</span>
											<span className="tp-meta-value">
												{skeletonLoading ? (
													<Skeleton width={150} />
												) : (
													formatDateTime(normalized?.invoice_date)
												)}
											</span>
										</div>
										<div className="tp-meta-item">
											<span className="tp-meta-label">{t("Payment Status")}</span>
											<span className="tp-meta-value">
												{skeletonLoading ? (
													<Skeleton width={90} height={22} />
												) : (
													<InvoiceStatusBadge
														status={paymentStatus}
														isSettled={record?.appointment_invoice?.is_settled === true}
													/>
												)}
											</span>
										</div>
									</div>

									<Row className="g-3 mb-3">
										<Col md="6" xl="4">
											<div className="tp-details-card">
												<div className="tp-details-card-title">
													<i className="fa fa-user-o" />
													<span>{t("Patient Details")}</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("Name")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? <Skeleton /> : patientName}
													</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("File No.")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? <Skeleton /> : fileNumber}
													</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("Address")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? <Skeleton /> : patientAddress}
													</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("Contact")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? <Skeleton /> : contactNumber}
													</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("Gender")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? <Skeleton /> : patientGender}
													</span>
												</div>
											</div>
										</Col>
										<Col md="6" xl="4">
											<div className="tp-details-card">
												<div className="tp-details-card-title">
													<i className="fa fa-stethoscope" />
													<span>{t("Doctor Details")}</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("Name")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? <Skeleton /> : doctorName}
													</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("Contact")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? <Skeleton /> : doctorContact}
													</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("Email")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? <Skeleton /> : doctorEmail}
													</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("Gender")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? <Skeleton /> : doctorGender}
													</span>
												</div>
											</div>
										</Col>
										<Col md="12" xl="4">
											<div className="tp-details-card">
												<div className="tp-details-card-title">
													<i className="fa fa-medkit" />
													<span>{t("Treatment Details")}</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("Treatment Package")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? (
															<Skeleton />
														) : (
															treatmentPackageDetails?.packageName || "-"
														)}
													</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("Total Sessions")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? (
															<Skeleton />
														) : (
															treatmentPackageDetails?.noOfSessions || "-"
														)}
													</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("Current Session")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? (
															<Skeleton />
														) : (
															treatmentPackageDetails?.currentSession || "-"
														)}
													</span>
												</div>
												<div className="tp-detail-row">
													<span className="tp-detail-label">{t("Treatment Start Date")}</span>
													<span className="tp-detail-value">
														{skeletonLoading ? (
															<Skeleton />
														) : (
															formatDateList(treatmentPackageDetails?.treatmentStartDate)
														)}
													</span>
												</div>
											</div>
										</Col>
									</Row>

									<Row className="g-3 mb-3">
										<Col xs="6" xl="3">
											<div className="tp-stat-card tp-stat-card--price">
												<div className="tp-stat-label">{t("Package Price")}</div>
												<div className="tp-stat-value">
													{formatAmount(
														treatmentPackageDetails?.packagePrice,
														currency,
														getCurrencySymbol
													)}
												</div>
												{treatmentPackageDetails?.additionalServicesTotal > 0 && (
													<div className="tp-stat-subtext">
														+{" "}
														{formatAmount(
															treatmentPackageDetails.additionalServicesTotal,
															currency,
															getCurrencySymbol
														)}{" "}
														{t("services")}
													</div>
												)}
											</div>
										</Col>
										<Col xs="6" xl="3">
											<div className="tp-stat-card tp-stat-card--collected">
												<div className="tp-stat-label">{t("Total Collected")}</div>
												<div className="tp-stat-value tp-stat-value--success">
													{formatAmount(
														treatmentPackageDetails?.totalCollected,
														currency,
														getCurrencySymbol
													)}
												</div>
											</div>
										</Col>
										<Col xs="6" xl="3">
											<div className="tp-stat-card tp-stat-card--payable">
												<div className="tp-stat-label">{t("Total Payable")}</div>
												<div className="tp-stat-value">
													{formatAmount(
														treatmentPackageDetails?.totalPayable,
														currency,
														getCurrencySymbol
													)}
												</div>
											</div>
										</Col>
										<Col xs="6" xl="3">
											<div className="tp-stat-card tp-stat-card--balance">
												<div className="tp-stat-label">{t("Remaining Balance")}</div>
												<div className="tp-stat-value tp-stat-value--danger">
													{formatAmount(
														treatmentPackageDetails?.remainingBalance,
														currency,
														getCurrencySymbol
													)}
												</div>
											</div>
										</Col>
									</Row>

									<div className="tp-section-box mb-3">
										<div className="tp-section-title">
											<i className="fa fa-list-ul" />
											<span>{t("Sessions")}</span>
										</div>
										<div className="table-responsive">
											<Table className="tp-table table-bordered table-hover mb-0">
												<thead>
													<tr>
														<th>#</th>
														<th>{t("Session")}</th>
														<th>{t("Description")}</th>
														<th>{t("Duration")}</th>
														<th>{t("Schedule")}</th>
														<th className="text-center">{t("Status")}</th>
													</tr>
												</thead>
												<tbody>
													{skeletonLoading ? (
														<tr>
															<td colSpan="6">
																<Skeleton count={3} />
															</td>
														</tr>
													) : treatmentPackageDetails?.sessions?.length > 0 ? (
														treatmentPackageDetails.sessions.map((session) => (
															<tr key={session.id}>
																<td>{session.index}</td>
																<td className="fw-semibold">{session.sessionName}</td>
																<td className="tp-muted-cell">{session.description}</td>
																<td>{session.duration}</td>
																<td>{session.schedule}</td>
																<td className="text-center">
																	<SessionStatusBadge status={session.status} />
																</td>
															</tr>
														))
													) : (
														<tr>
															<td colSpan="6" className="text-center text-muted">
																{t("No sessions found.")}
															</td>
														</tr>
													)}
												</tbody>
											</Table>
										</div>
									</div>

									{additionalServices.length > 0 && (
										<div className="tp-section-box mb-3">
											<div className="tp-section-title">
												<i className="fa fa-plus-square-o" />
												<span>{t("Additional Services")}</span>
											</div>
											<div className="table-responsive">
												<Table className="tp-table table-bordered table-hover mb-0">
													<thead>
														<tr>
															<th>#</th>
															<th>{t("Service")}</th>
															<th>{t("Type")}</th>
															<th className="text-center">{t("Qty")}</th>
															<th className="text-end">{t("Price")}</th>
															<th className="text-end">{t("Amount")}</th>
														</tr>
													</thead>
													<tbody>
														{additionalServices.map((service, index) => (
															<tr key={service.id || index}>
																<td>{index + 1}</td>
																<td className="fw-semibold">{service.service_name}</td>
																<td style={{ textTransform: "capitalize" }}>
																	{formatInvoiceType(service.service_type)}
																</td>
																<td className="text-center">{service.quantity}</td>
																<td className="text-end">
																	{formatAmount(
																		service.unit_price ?? service.price,
																		service.currency || currency,
																		getCurrencySymbol
																	)}
																</td>
																<td className="text-end fw-semibold">
																	{formatAmount(
																		service.amount,
																		service.currency || currency,
																		getCurrencySymbol
																	)}
																</td>
															</tr>
														))}
													</tbody>
												</Table>
											</div>
										</div>
									)}

									{labInvestigations.length > 0 && (
										<div className="tp-section-box mb-3">
											<div className="tp-section-title">
												<i className="fa fa-flask" />
												<span>{t("Lab Investigations")}</span>
											</div>
											<div className="table-responsive">
												<Table className="tp-table table-bordered table-hover mb-0">
													<thead>
														<tr>
															<th>#</th>
															<th>{t("Test")}</th>
															<th>{t("Type")}</th>
															<th className="text-center">{t("Qty")}</th>
															<th className="text-end">{t("Price")}</th>
															<th className="text-end">{t("Amount")}</th>
														</tr>
													</thead>
													<tbody>
														{labInvestigations.map((test, index) => (
															<tr key={test.id || test.test_id || index}>
																<td>{index + 1}</td>
																<td className="fw-semibold">{test.test_name}</td>
																<td style={{ textTransform: "capitalize" }}>
																	{formatInvoiceType(test.type)}
																</td>
																<td className="text-center">{test.quantity}</td>
																<td className="text-end">
																	{test.priceResolved ? (
																		formatAmount(
																			test.unit_price,
																			test.currency || currency,
																			getCurrencySymbol
																		)
																	) : (
																		<span
																			className="text-muted fst-italic"
																			title={t(
																				"Price not set in master. It can be entered while collecting payment."
																			)}
																		>
																			{t("N/A")}
																		</span>
																	)}
																</td>
																<td className="text-end fw-semibold">
																	{test.priceResolved ? (
																		formatAmount(
																			test.amount,
																			test.currency || currency,
																			getCurrencySymbol
																		)
																	) : (
																		<span
																			className="text-muted fst-italic"
																			title={t(
																				"Amount not set in master. It can be entered while collecting payment."
																			)}
																		>
																			{t("N/A")}
																		</span>
																	)}
																</td>
															</tr>
														))}
													</tbody>
												</Table>
											</div>
										</div>
									)}

									{showPrescriptionSection && (
										<div className="tp-section-box mb-3">
											<div className="tp-section-title">
												<i className="fa fa-medkit" />
												<span>{t("Prescription Medicines")}</span>
											</div>
											<div className="table-responsive">
												<Table className="tp-table table-bordered table-hover mb-0">
													<thead>
														<tr>
															<th>{t("Item")}</th>
															<th className="text-center">{t("Date")}</th>
															<th className="text-end">{t("MRP")}</th>
															<th className="text-center">{t("Qty")}</th>
															<th className="text-end">{t("Taxable")}</th>
															<th className="text-end">{t("Tax")}</th>
															<th className="text-end">{t("Gross")}</th>
														</tr>
													</thead>
													<tbody>
														{prescriptionBillingItems.map((item, index) => (
															<tr key={item.item_id || index}>
																<td>
																	<div className="fw-semibold">
																		{item.item || item.brand_name}
																	</div>
																	{item.medicine_name && (
																		<div className="tp-muted-cell small">
																			{item.medicine_name}
																		</div>
																	)}
																	{item.product_form && (
																		<span className="tp-form-tag">{item.product_form}</span>
																	)}
																</td>
																<td className="text-center">
																	{item.date ? formatDateList(item.date) : "-"}
																</td>
																<td className="text-end">
																	{formatAmount(item.unit_price, currency, getCurrencySymbol)}
																</td>
																<td className="text-center">{item.qty}</td>
																<td className="text-end">
																	{formatAmount(item.sub_total, currency, getCurrencySymbol)}
																</td>
																<td className="text-end">
																	{formatAmount(item.tax, currency, getCurrencySymbol)}
																	{item.tax_percentage > 0 && (
																		<small className="tp-muted-cell d-block">
																			{item.tax_percentage}%
																		</small>
																	)}
																</td>
																<td className="text-end fw-semibold">
																	{formatAmount(item.net_total, currency, getCurrencySymbol)}
																</td>
															</tr>
														))}
													</tbody>
												</Table>
											</div>
										</div>
									)}

									<Row className="g-3">
										<Col lg="8">
											<div className="tp-section-box h-100">
												<div className="tp-section-title">
													<i className="fa fa-history" />
													<span>{t("Treatment & Payment History")}</span>
												</div>
												<div className="table-responsive">
													<Table className="tp-table table-hover mb-0">
														<thead>
															<tr>
																<th>{t("Date")}</th>
																<th>{t("Type")}</th>
																<th>{t("Description")}</th>
																<th className="text-end">{t("Paid Amount")}</th>
																<th>{t("Payment Method")}</th>
															</tr>
														</thead>
														<tbody>
															{treatmentPaymentHistory.length > 0 ? (
																treatmentPaymentHistory.map((payment, index) => (
																	<tr key={index}>
																		<td>{formatDateTime(payment.date)}</td>
																		<td>{payment.type}</td>
																		<td className="tp-muted-cell">{payment.description}</td>
																		<td className="text-end fw-semibold">
																			{formatAmount(
																				payment.paid_amount,
																				payment.currency,
																				getCurrencySymbol
																			)}
																		</td>
																		<td style={{ textTransform: "capitalize" }}>
																			{payment.payment_mode || "-"}
																		</td>
																	</tr>
																))
															) : (
																<tr>
																	<td colSpan="5" className="text-center text-muted">
																		{t("No billing history available.")}
																	</td>
																</tr>
															)}
														</tbody>
													</Table>
												</div>
											</div>
										</Col>
										<Col lg="4">
											<div className="summary-box h-100">
												<h6 className="fw-bold mb-3">{t("Invoice Summary")}</h6>
												<div className="summary-row">
													<span>{t("Package Price")}</span>
													<strong>
														{formatAmount(
															treatmentPackageDetails?.packagePrice,
															currency,
															getCurrencySymbol
														)}
													</strong>
												</div>
												{treatmentPackageDetails?.additionalServicesTotal > 0 && (
													<div className="summary-row">
														<span>{t("Additional Services")}</span>
														<strong>
															{formatAmount(
																treatmentPackageDetails.additionalServicesTotal,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
												)}
												{treatmentPackageDetails?.pharmacyTotal > 0 && (
													<div className="summary-row">
														<span>{t("Pharmacy")}</span>
														<strong>
															{formatAmount(
																treatmentPackageDetails.pharmacyTotal,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
												)}
												<div className="summary-row">
													<span>{t("Subtotal")}</span>
													<strong>
														{formatAmount(
															financialSummary.sub_total,
															currency,
															getCurrencySymbol
														)}
													</strong>
												</div>
												{financialSummary.discount > 0 && (
													<div className="summary-row">
														<span>{t("Discount")}</span>
														<strong>
															{formatAmount(
																financialSummary.discount,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
												)}
												{financialSummary.tax > 0 && (
													<div className="summary-row">
														<span>{t("Tax")}</span>
														<strong>
															{formatAmount(
																financialSummary.tax,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
												)}
												<div className="summary-row net-total-row">
													<span>{t("Total Payable")}</span>
													<strong>
														{formatAmount(
															treatmentPackageDetails?.totalPayable,
															currency,
															getCurrencySymbol
														)}
													</strong>
												</div>
												<div className="summary-row">
													<span>{t("Total Collected")}</span>
													<strong className="tp-summary-collected">
														{formatAmount(
															treatmentPackageDetails?.totalCollected,
															currency,
															getCurrencySymbol
														)}
													</strong>
												</div>
												{Number(treatmentPackageDetails?.remainingBalance) > 0 && (
													<div className="summary-row due-row">
														<span>{t("Remaining Balance")}</span>
														<strong className="due-value">
															{formatAmount(
																treatmentPackageDetails?.remainingBalance,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
												)}
											</div>
										</Col>
									</Row>
								</div>
							) : (
								<>
							<div className="tp-meta-strip mb-3">
								<div className="tp-meta-item">
									<span className="tp-meta-label">{t("Invoice Number")}</span>
									<span className="tp-meta-value">
										{skeletonLoading ? (
											<Skeleton width={130} />
										) : (
											normalized?.invoice_no || "-"
										)}
									</span>
								</div>
								<div className="tp-meta-item">
									<span className="tp-meta-label">{t("Invoice Date")}</span>
									<span className="tp-meta-value">
										{skeletonLoading ? (
											<Skeleton width={150} />
										) : (
											formatDateTime(normalized?.invoice_date)
										)}
									</span>
								</div>
								<div className="tp-meta-item">
									<span className="tp-meta-label">{t("Doctor")}</span>
									<span className="tp-meta-value">
										{skeletonLoading ? (
											<Skeleton width={130} />
										) : (
											record?.doctor?.name || "-"
										)}
									</span>
								</div>
								<div className="tp-meta-item">
									<span className="tp-meta-label">{t("Payment Status")}</span>
									<span className="tp-meta-value">
										{skeletonLoading ? (
											<Skeleton width={90} height={22} />
										) : (
											<InvoiceStatusBadge
												status={paymentStatus}
												isSettled={record?.appointment_invoice?.is_settled === true}
											/>
										)}
									</span>
								</div>
							</div>

							<div className="tp-meta-strip mb-4">
								<div className="tp-meta-item">
									<span className="tp-meta-label">{t("Name")}</span>
									<span className="tp-meta-value">
										{skeletonLoading ? <Skeleton width={130} /> : patientName}
									</span>
								</div>
								<div className="tp-meta-item">
									<span className="tp-meta-label">{t("File Number")}</span>
									<span className="tp-meta-value">
										{skeletonLoading ? <Skeleton width={110} /> : fileNumber}
									</span>
								</div>
								<div className="tp-meta-item">
									<span className="tp-meta-label">{t("Contact Number")}</span>
									<span className="tp-meta-value">
										{skeletonLoading ? <Skeleton width={130} /> : contactNumber}
									</span>
								</div>
								<div className="tp-meta-item">
									<span className="tp-meta-label">{t("Email")}</span>
									<span className="tp-meta-value">
										{skeletonLoading ? <Skeleton width={150} /> : email}
									</span>
								</div>
								{record?.consultation_reason && (
									<div className="tp-meta-item">
										<span className="tp-meta-label">{t("Consultation Reason")}</span>
										<span className="tp-meta-value">{record.consultation_reason}</span>
									</div>
								)}
							</div>

							<div className="table-responsive mb-4">
								<Table className="table-bordered mb-0">
									<thead>
										<tr>
											<th>#</th>
											<th>{t("Service / Item")}</th>
											<th>{t("Type")}</th>
											<th className="text-center">{t("Qty")}</th>
											<th className="text-end">{t("Price")}</th>
											<th className="text-end">{t("Amount")}</th>
										</tr>
									</thead>
									<tbody>
										{skeletonLoading ? (
											<tr>
												<td colSpan="6">
													<Skeleton count={2} />
												</td>
											</tr>
										) : serviceLines.length > 0 ? (
											serviceLines.map((item, index) => (
												<tr key={item.id || index}>
													<td>{index + 1}</td>
													<td>{item.service_name}</td>
													<td style={{ textTransform: "capitalize" }}>
														{formatInvoiceType(item.service_type)}
													</td>
													<td className="text-center">{item.quantity}</td>
													<td className="text-end">
														{formatAmount(item.charges, currency, getCurrencySymbol)}
													</td>
													<td className="text-end">
														{formatAmount(item.amount, currency, getCurrencySymbol)}
													</td>
												</tr>
											))
										) : (
											<tr>
												<td colSpan="6" className="text-center text-muted">
													{t("No services found.")}
												</td>
											</tr>
										)}
									</tbody>
									<tfoot>
										<tr>
											<td colSpan="5" className="text-end fw-bold">
												{t("Subtotal")}:
											</td>
											<td className="text-end fw-bold">
												{formatAmount(financialSummary.sub_total, currency, getCurrencySymbol)}
											</td>
										</tr>
									</tfoot>
								</Table>
							</div>

							<Row className="g-4">
								<Col lg="8">
									<div className="payment-history-box">
										<h6 className="fw-bold mb-3">{t("Payment History")}</h6>
										<div className="table-responsive">
											<Table className="table-bordered mb-0">
												<thead>
													<tr>
														<th>#</th>
														<th>{t("Payment Date")}</th>
														<th>{t("Paid Amount")}</th>
														<th>{t("Payment Method")}</th>
														<th>{t("Receipt No.")}</th>
													</tr>
												</thead>
												<tbody>
													{paymentHistory.length > 0 ? (
														paymentHistory.map((payment, index) => (
															<tr key={index}>
																<td>{index + 1}</td>
																<td>{formatDateTime(payment.payment_date)}</td>
																<td>
																	{formatAmount(
																		payment.paid_amount,
																		payment.currency,
																		getCurrencySymbol
																	)}
																</td>
																<td style={{ textTransform: "capitalize" }}>
																	{payment.payment_mode || "-"}
																</td>
																<td>{payment.receipt_number || "-"}</td>
															</tr>
														))
													) : (
														<tr>
															<td colSpan="5" className="text-center text-muted">
																{t("No billing history available.")}
															</td>
														</tr>
													)}
												</tbody>
												<tfoot>
													<tr>
														<td colSpan="4" className="text-end fw-bold">
															{t("Total Paid")}:
														</td>
														<td className="fw-bold">
															<PatientAccountPaidAmount
																amount={financialSummary.total_paid}
																currency={currency}
																foreignPayments={financialSummary.foreign_payments}
																getCurrencySymbol={getCurrencySymbol}
															/>
														</td>
													</tr>
												</tfoot>
											</Table>
										</div>
									</div>
								</Col>
								<Col lg="4">
									<div className="summary-box">
										<h6 className="fw-bold mb-3">{t("Invoice Summary")}</h6>
										{(financialSummary.services_total > 0 ||
											financialSummary.investigation_total > 0 ||
											financialSummary.pharmacy_total > 0) && (
											<>
												{financialSummary.services_total > 0 && (
													<div className="summary-row">
														<span>{t("Services")}</span>
														<strong>
															{formatAmount(
																financialSummary.services_total,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
												)}
												{financialSummary.investigation_total > 0 && (
													<div className="summary-row">
														<span>{t("Lab")}</span>
														<strong>
															{formatAmount(
																financialSummary.investigation_total,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
												)}
												{financialSummary.pharmacy_total > 0 && (
													<div className="summary-row">
														<span>{t("Pharmacy")}</span>
														<strong>
															{formatAmount(
																financialSummary.pharmacy_total,
																currency,
																getCurrencySymbol
															)}
														</strong>
													</div>
												)}
											</>
										)}
										<div className="summary-row">
											<span>{t("Subtotal")}</span>
											<strong>
												{formatAmount(financialSummary.sub_total, currency, getCurrencySymbol)}
											</strong>
										</div>
										<div className="summary-row">
											<span>{t("Discount")}</span>
											<strong>
												{formatAmount(financialSummary.discount, currency, getCurrencySymbol)}
											</strong>
										</div>
										{financialSummary.tax > 0 && (
											<div className="summary-row">
												<span>{t("Tax")}</span>
												<strong>
													{formatAmount(financialSummary.tax, currency, getCurrencySymbol)}
												</strong>
											</div>
										)}
										<div className="summary-row net-total-row">
											<span>{t("Net Total")}</span>
											<strong>
												{formatAmount(financialSummary.net_total, currency, getCurrencySymbol)}
											</strong>
										</div>
										<div className="summary-row">
											<span>{t("Total Paid")}</span>
											<PatientAccountPaidAmount
												className="text-end"
												amount={financialSummary.total_paid}
												currency={currency}
												foreignPayments={financialSummary.foreign_payments}
												getCurrencySymbol={getCurrencySymbol}
											/>
										</div>
										{showDueRow && (
											<div className="summary-row due-row">
												<span>{t("Due")}</span>
												<strong className="due-value">
													{formatAmount(financialSummary.due, currency, getCurrencySymbol)}
												</strong>
											</div>
										)}
									</div>
								</Col>
							</Row>

							{record?.appointment_invoice?.invoice_type ? (
								<div className="mt-3 text-muted small">
									{t("Type")}:{" "}
									{formatInvoiceType(record.appointment_invoice.invoice_type)}
								</div>
							) : null}
								</>
							)}
						</div>
					</div>
				</div>
			</div>

			<InvoicePreviewModal
				isOpen={previewModal}
				toggle={closeDocumentPreview}
				invoiceUrl={previewUrl}
				invoiceNo={normalized?.invoice_no}
				title={previewTitle}
			/>

			<CollectPaymentModal
				isOpen={collectModal}
				toggle={() => setCollectModal(false)}
				invoice={record}
				onSubmit={handleCollectSubmit}
				isSubmitting={isSubmittingPayment}
			/>
		</>
	);
};

export default PatientAccountDetails;
